import { useState, useEffect, useCallback } from 'react';
import type {
  AIProviderType,
  GenerationModelSettings,
  TranslationModelSettings,
  ModelSettings,
  ModelSettingsResponse,
  PromptDefaults,
  LearningLanguage,
  GeminiThinkingLevel,
} from '../types';
import { fetchModelSettings, saveModelSettings } from '../services/api';
import cfGenerationModels from '../data/cf-generation-models.json';
import cfTranslationModels from '../data/cf-translation-models.json';
import geminiModelsData from '../data/gemini-models.json';
import openrouterModelsData from '../data/openrouter-models.json';
import { formatCFPricing, formatGeminiPricing, formatOpenRouterPricing } from '../utils/pricing';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import { getLearningLanguageName, supportsVerbPractice } from '../utils/learningLanguage';
import { buildVerbCardHref } from '../utils/verbPractice';
import './ModelSettingsPage.css';

interface Props {
  onBack: () => void;
  onNavigateToChat?: () => void;
  onNavigateToKon?: () => void;
  learningLanguage?: LearningLanguage;
  onLearningLanguageChange?: (language: LearningLanguage) => void;
  topicEnabled?: boolean;
  onTopicEnabledChange?: (enabled: boolean) => void;
  onDisplayPreferencesChange?: (displayPreferences: {
    showModelLabel: boolean;
    showDebugInfo: boolean;
  }) => void;
  language?: Language;
}

interface CFModel {
  value: string;
  label: string;
  info?: string;
}

interface GeminiModel {
  value: string;
  label: string;
  info: string;
}

interface OpenRouterModel {
  value: string;
  label: string;
  info: string;
}

const CF_GENERATION_MODELS: CFModel[] = cfGenerationModels;
const CF_TRANSLATION_MODELS: CFModel[] = cfTranslationModels;
const GEMINI_MODELS: GeminiModel[] = geminiModelsData;
const OPENROUTER_MODELS: OpenRouterModel[] = openrouterModelsData;

const PROVIDERS: { value: AIProviderType; label: string }[] = [
  { value: 'cloudflare', label: 'Cloudflare AI Workers' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openrouter', label: 'OpenRouter' },
];

const GEMINI_THINKING_BUDGET_MODELS = [
  'gemini-2.5-flash',
];

const GEMINI_FLASH_THINKING_LEVELS: GeminiThinkingLevel[] = ['minimal', 'low', 'medium', 'high'];
const GEMINI_THINKING_LEVELS_BY_MODEL: Record<string, GeminiThinkingLevel[]> = {
  'gemini-3.7-flash': ['low', 'medium', 'high'],
  'gemini-3.5-flash-lite': GEMINI_FLASH_THINKING_LEVELS,
};

function isGeminiThinkingBudgetModel(model: string) {
  return GEMINI_THINKING_BUDGET_MODELS.includes(model);
}

function isGeminiThinkingLevelModel(model: string) {
  return model in GEMINI_THINKING_LEVELS_BY_MODEL;
}

function getGeminiThinkingLevels(model: string) {
  return GEMINI_THINKING_LEVELS_BY_MODEL[model] ?? GEMINI_FLASH_THINKING_LEVELS;
}

function normalizeGeminiThinkingLevel(model: string, level?: GeminiThinkingLevel): GeminiThinkingLevel {
  const levels = getGeminiThinkingLevels(model);
  if (level && levels.includes(level)) return level;
  return levels[0];
}

export default function ModelSettingsPage({
  onBack,
  onNavigateToChat,
  onNavigateToKon,
  learningLanguage = 'pl',
  onLearningLanguageChange,
  topicEnabled = false,
  onTopicEnabledChange,
  onDisplayPreferencesChange,
  language = 'en',
}: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [promptDefaults, setPromptDefaults] = useState<PromptDefaults | null>(null);

  // Generation settings
  const [genProvider, setGenProvider] = useState<AIProviderType>('cloudflare');
  const [genModel, setGenModel] = useState('@cf/google/gemma-4-26b-a4b-it');
  const [genMaxTokens, setGenMaxTokens] = useState(512);
  const [genTemperature, setGenTemperature] = useState(0.7);
  const [genTopP, setGenTopP] = useState(0.95);
  const [genTopK, setGenTopK] = useState(40);
  const [genApiKeys, setGenApiKeys] = useState<Partial<Record<AIProviderType, string>>>({});
  const [genThinkingEnabled, setGenThinkingEnabled] = useState(false);
  const [genThinkingBudget, setGenThinkingBudget] = useState(1024);
  const [genThinkingLevel, setGenThinkingLevel] = useState<GeminiThinkingLevel>('minimal');
  const [generatePromptTemplate, setGeneratePromptTemplate] = useState('');
  const [verbPromptTemplate, setVerbPromptTemplate] = useState('');
  const [tenseInstructionTemplate, setTenseInstructionTemplate] = useState('');
  const [topicInstructionTemplate, setTopicInstructionTemplate] = useState('');
  const [caseInstructionTemplate, setCaseInstructionTemplate] = useState('');

  // Translation settings
  const [transProvider, setTransProvider] = useState<AIProviderType>('cloudflare');
  const [transModel, setTransModel] = useState('@cf/meta/m2m100-1.2b');
  const [transTemperature, setTransTemperature] = useState(0.3);
  const [transMaxTokens, setTransMaxTokens] = useState(1024);
  const [transTopP, setTransTopP] = useState(0.95);
  const [transTopK, setTransTopK] = useState(40);
  const [transApiKeys, setTransApiKeys] = useState<Partial<Record<AIProviderType, string>>>({});
  const [transThinkingEnabled, setTransThinkingEnabled] = useState(false);
  const [transThinkingBudget, setTransThinkingBudget] = useState(1024);
  const [transThinkingLevel, setTransThinkingLevel] = useState<GeminiThinkingLevel>('minimal');
  const [translatePromptTemplate, setTranslatePromptTemplate] = useState('');

  // Display preferences
  const [showModelLabel, setShowModelLabel] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Defaults for reset
  const [defaults, setDefaults] = useState<ModelSettingsResponse['defaults'] | null>(null);
  const sourceLanguageName = getLearningLanguageName(learningLanguage);
  const verbPracticeAvailable = supportsVerbPractice(learningLanguage);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data: ModelSettingsResponse | null = await fetchModelSettings();
        if (cancelled || !data) return;

        setPromptDefaults(data.promptDefaults);
        setDefaults(data.defaults);

        const ms = data.modelSettings;
        if (ms) {
          // Generation
          setGenProvider(ms.generation.provider);
          setGenModel(ms.generation.model);
          setGenMaxTokens(ms.generation.maxTokens);
          setGenTemperature(ms.generation.temperature);
          if (ms.generation.topP !== undefined) setGenTopP(ms.generation.topP);
          if (ms.generation.topK !== undefined) setGenTopK(ms.generation.topK);
          if (ms.generation.apiKeys) {
            setGenApiKeys(ms.generation.apiKeys);
          } else if (ms.generation.apiKey) {
            setGenApiKeys({ [ms.generation.provider]: ms.generation.apiKey });
          }
          if (ms.generation.thinkingBudget !== undefined && ms.generation.thinkingBudget > 0) {
            setGenThinkingEnabled(true);
            setGenThinkingBudget(ms.generation.thinkingBudget);
          }
          setGenThinkingLevel(normalizeGeminiThinkingLevel(ms.generation.model, ms.generation.thinkingLevel));
          if (ms.generation.generatePromptTemplate) setGeneratePromptTemplate(ms.generation.generatePromptTemplate);
          if (ms.generation.verbPromptTemplate) setVerbPromptTemplate(ms.generation.verbPromptTemplate);
          if (ms.generation.tenseInstructionTemplate) setTenseInstructionTemplate(ms.generation.tenseInstructionTemplate);
          if (ms.generation.topicInstructionTemplate) setTopicInstructionTemplate(ms.generation.topicInstructionTemplate);
          setCaseInstructionTemplate(ms.generation.caseInstructionTemplate || data.promptDefaults.caseInstructionTemplate);

          // Translation
          setTransProvider(ms.translation.provider);
          setTransModel(ms.translation.model);
          if (ms.translation.temperature !== undefined) setTransTemperature(ms.translation.temperature);
          if (ms.translation.maxTokens !== undefined) setTransMaxTokens(ms.translation.maxTokens);
          if (ms.translation.topP !== undefined) setTransTopP(ms.translation.topP);
          if (ms.translation.topK !== undefined) setTransTopK(ms.translation.topK);
          if (ms.translation.apiKeys) {
            setTransApiKeys(ms.translation.apiKeys);
          } else if (ms.translation.apiKey) {
            setTransApiKeys({ [ms.translation.provider]: ms.translation.apiKey });
          }
          if (ms.translation.thinkingBudget !== undefined && ms.translation.thinkingBudget > 0) {
            setTransThinkingEnabled(true);
            setTransThinkingBudget(ms.translation.thinkingBudget);
          }
          setTransThinkingLevel(normalizeGeminiThinkingLevel(ms.translation.model, ms.translation.thinkingLevel));
          if (ms.translation.translatePromptTemplate) setTranslatePromptTemplate(ms.translation.translatePromptTemplate);

          // Display preferences
          if (ms.showModelLabel !== undefined) setShowModelLabel(ms.showModelLabel);
          if (ms.showDebugInfo !== undefined) setShowDebugInfo(ms.showDebugInfo);
        } else {
          // Apply defaults
          const gd = data.defaults.generation;
          const td = data.defaults.translation;
          setGenProvider(gd.provider);
          setGenModel(gd.model);
          setGenMaxTokens(gd.maxTokens);
          setGenTemperature(gd.temperature);
          setTransProvider(td.provider);
          setTransModel(td.model);
          setCaseInstructionTemplate(data.promptDefaults.caseInstructionTemplate);
        }
      } catch {
        // ignore load error
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleGenProviderChange = useCallback((provider: AIProviderType) => {
    setGenProvider(provider);
    if (provider === 'cloudflare') {
      setGenModel('@cf/google/gemma-4-26b-a4b-it');
      setGenMaxTokens(512);
      setGenTemperature(0.7);
    } else if (provider === 'openrouter') {
      setGenModel('openai/gpt-5.6-luna');
      setGenMaxTokens(1024);
      setGenTemperature(0.7);
      setGenTopP(0.95);
      setGenTopK(40);
    } else {
      setGenModel('gemini-3.7-flash');
      setGenMaxTokens(1024);
      setGenTemperature(0.7);
      setGenTopP(0.95);
      setGenTopK(40);
      setGenThinkingLevel('low');
    }
  }, []);

  const handleTransProviderChange = useCallback((provider: AIProviderType) => {
    setTransProvider(provider);
    if (provider === 'cloudflare') {
      setTransModel('@cf/meta/m2m100-1.2b');
      setTransTemperature(0.3);
      setTransMaxTokens(1024);
    } else if (provider === 'openrouter') {
      setTransModel('openai/gpt-5.6-luna');
      setTransTemperature(0.3);
      setTransMaxTokens(1024);
      setTransTopP(0.95);
      setTransTopK(40);
    } else {
      setTransModel('gemini-3.5-flash-lite');
      setTransTemperature(0.3);
      setTransMaxTokens(1024);
      setTransTopP(0.95);
      setTransTopK(40);
      setTransThinkingLevel('minimal');
    }
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    try {
      const genUsesThinkingBudget = genProvider === 'gemini' && isGeminiThinkingBudgetModel(genModel);
      const genUsesThinkingLevel = genProvider === 'gemini' && isGeminiThinkingLevelModel(genModel);
      const generation: GenerationModelSettings = {
        provider: genProvider,
        model: genModel,
        maxTokens: genMaxTokens,
        temperature: genTemperature,
        ...(genProvider === 'gemini' || genProvider === 'openrouter' ? { topP: genTopP, topK: genTopK } : {}),
        ...(genUsesThinkingBudget ? { thinkingBudget: genThinkingEnabled ? genThinkingBudget : 0 } : {}),
        ...(genUsesThinkingLevel ? { thinkingLevel: normalizeGeminiThinkingLevel(genModel, genThinkingLevel) } : {}),
        ...(genApiKeys[genProvider] ? { apiKey: genApiKeys[genProvider] } : {}),
        ...(Object.keys(genApiKeys).length > 0 ? { apiKeys: genApiKeys } : {}),
        ...(generatePromptTemplate ? { generatePromptTemplate } : {}),
        ...(verbPromptTemplate ? { verbPromptTemplate } : {}),
        ...(tenseInstructionTemplate ? { tenseInstructionTemplate } : {}),
        ...(topicInstructionTemplate ? { topicInstructionTemplate } : {}),
        ...(caseInstructionTemplate ? { caseInstructionTemplate } : {}),
      };

      const transUsesThinkingBudget = transProvider === 'gemini' && isGeminiThinkingBudgetModel(transModel);
      const transUsesThinkingLevel = transProvider === 'gemini' && isGeminiThinkingLevelModel(transModel);
      const translation: TranslationModelSettings = {
        provider: transProvider,
        model: transModel,
        ...(transProvider !== 'cloudflare' || !transModel.includes('m2m100') ? {
          temperature: transTemperature,
          maxTokens: transMaxTokens,
        } : {}),
        ...(transProvider === 'gemini' || transProvider === 'openrouter' ? { topP: transTopP, topK: transTopK } : {}),
        ...(transUsesThinkingBudget ? { thinkingBudget: transThinkingEnabled ? transThinkingBudget : 0 } : {}),
        ...(transUsesThinkingLevel ? { thinkingLevel: normalizeGeminiThinkingLevel(transModel, transThinkingLevel) } : {}),
        ...(transApiKeys[transProvider] ? { apiKey: transApiKeys[transProvider] } : {}),
        ...(Object.keys(transApiKeys).length > 0 ? { apiKeys: transApiKeys } : {}),
        ...(translatePromptTemplate ? { translatePromptTemplate } : {}),
      };

      const modelSettings: ModelSettings = { generation, translation, showModelLabel, showDebugInfo };
      await saveModelSettings(modelSettings);
      onDisplayPreferencesChange?.({ showModelLabel, showDebugInfo });
      setSaved(true);
      onBack();
    } catch {
      alert('Failed to save model settings');
    } finally {
      setSaving(false);
    }
  }, [
    genProvider, genModel, genMaxTokens, genTemperature, genTopP, genTopK, genApiKeys,
    genThinkingEnabled, genThinkingBudget, genThinkingLevel,
    generatePromptTemplate, verbPromptTemplate, tenseInstructionTemplate, topicInstructionTemplate, caseInstructionTemplate,
    transProvider, transModel, transTemperature, transMaxTokens, transTopP, transTopK, transApiKeys,
    transThinkingEnabled, transThinkingBudget, transThinkingLevel,
    translatePromptTemplate, showModelLabel, showDebugInfo, onBack, onDisplayPreferencesChange,
  ]);

  const handleReset = useCallback(async () => {
    if (!window.confirm(t(language, 'settingsPage.resetConfirm'))) return;
    if (!defaults) return;
    const gd = defaults.generation;
    const td = defaults.translation;
    setGenProvider(gd.provider);
    setGenModel(gd.model);
    setGenMaxTokens(gd.maxTokens);
    setGenTemperature(gd.temperature);
    setGenTopP(gd.topP ?? 0.95);
    setGenTopK(gd.topK ?? 40);
    setGenApiKeys({});
    setGenThinkingEnabled(false);
    setGenThinkingBudget(1024);
    setGenThinkingLevel(normalizeGeminiThinkingLevel(gd.model));
    setGeneratePromptTemplate('');
    setVerbPromptTemplate('');
    setTenseInstructionTemplate('');
    setTopicInstructionTemplate('');
    setCaseInstructionTemplate(promptDefaults?.caseInstructionTemplate || '');
    setTransProvider(td.provider);
    setTransModel(td.model);
    setTransTemperature(td.temperature ?? 0.3);
    setTransMaxTokens(td.maxTokens ?? 1024);
    setTransTopP(td.topP ?? 0.95);
    setTransTopK(td.topK ?? 40);
    setTransApiKeys({});
    setTransThinkingEnabled(false);
    setTransThinkingBudget(1024);
    setTransThinkingLevel(normalizeGeminiThinkingLevel(td.model));
    setTranslatePromptTemplate('');
    setShowModelLabel(false);
    setShowDebugInfo(false);
    try {
      const modelSettings: ModelSettings = {
        generation: gd,
        translation: td,
        showModelLabel: false,
        showDebugInfo: false,
      };
      await saveModelSettings(modelSettings);
      onDisplayPreferencesChange?.({ showModelLabel: false, showDebugInfo: false });
    } catch {
      // ignore save errors on reset
    }
  }, [defaults, language, onDisplayPreferencesChange, promptDefaults]);

  if (loading) {
    return (
      <div className="model-settings-page">
        <header>
          <h1>{t(language, 'settingsPage.title')}</h1>
          <nav className="nav-links">
            <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>{t(language, 'kon.backToMain')}</a>
            {onNavigateToKon && verbPracticeAvailable && <a href={buildVerbCardHref(undefined, undefined, learningLanguage)} onClick={(e) => { e.preventDefault(); onNavigateToKon(); }}>{t(language, 'nav.koniugacja')}</a>}
            {onNavigateToChat && <a href="/chat" onClick={(e) => { e.preventDefault(); onNavigateToChat(); }}>{t(language, 'nav.chat')}</a>}
          </nav>
        </header>
        <div className="loading"><div className="spinner" /><span>Loading settings…</span></div>
      </div>
    );
  }

  const genModelOptions = genProvider === 'cloudflare' ? CF_GENERATION_MODELS : genProvider === 'openrouter' ? OPENROUTER_MODELS : GEMINI_MODELS;
  const transModelOptions = transProvider === 'cloudflare' ? CF_TRANSLATION_MODELS : transProvider === 'openrouter' ? OPENROUTER_MODELS : GEMINI_MODELS;
  const isM2M = transProvider === 'cloudflare' && transModel.includes('m2m100');
  const genTotalTokensWithThinking = genMaxTokens + genThinkingBudget;
  const transTotalTokensWithThinking = transMaxTokens + transThinkingBudget;
  const genThinkingLevelOptions = getGeminiThinkingLevels(genModel);
  const transThinkingLevelOptions = getGeminiThinkingLevels(transModel);

  return (
    <div className="model-settings-page">
      <header>
        <h1>{t(language, 'settingsPage.title')}</h1>
        <nav className="nav-links">
          <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>{t(language, 'kon.backToMain')}</a>
          {onNavigateToKon && verbPracticeAvailable && <a href={buildVerbCardHref(undefined, undefined, learningLanguage)} onClick={(e) => { e.preventDefault(); onNavigateToKon(); }}>{t(language, 'nav.koniugacja')}</a>}
          {onNavigateToChat && <a href="/chat" onClick={(e) => { e.preventDefault(); onNavigateToChat(); }}>{t(language, 'nav.chat')}</a>}
        </nav>
      </header>

      <div className="settings-sections">
        <section className="settings-section">
          <h2>{t(language, 'settingsPage.learning')}</h2>

          <div className="field">
            <label>{t(language, 'settingsPage.learningLanguage')}</label>
            <select
              value={learningLanguage}
              onChange={(e) => onLearningLanguageChange?.(e.target.value as LearningLanguage)}
            >
              <option value="pl">{t(language, 'learningLanguage.polish')}</option>
              <option value="lt">{t(language, 'learningLanguage.lithuanian')}</option>
            </select>
            <span className="field-hint">
              This controls which language is used for sentence generation, translation to Russian, and verb practice decks.
            </span>
          </div>
        </section>

        {/* ── Generation Section ── */}
        <section className="settings-section">
          <h2>{t(language, 'settingsPage.generation')}</h2>

          <div className="field">
            <label>Provider</label>
            <select value={genProvider} onChange={(e) => handleGenProviderChange(e.target.value as AIProviderType)}>
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <span className="field-hint">Choose the AI backend for generating {sourceLanguageName} sentences. Cloudflare AI Workers runs models in Cloudflare’s serverless infrastructure. Google Gemini and OpenRouter require an API key (or a corresponding env secret).</span>
          </div>

          <div className="field">
            <label>Model</label>
            <select
              value={genModel}
              onChange={(e) => {
                const model = e.target.value;
                setGenModel(model);
                setGenThinkingLevel(normalizeGeminiThinkingLevel(model, genThinkingLevel));
              }}
            >
              {genModelOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <span className="field-hint">
              {genProvider === 'cloudflare'
                ? formatCFPricing(genModel, CF_GENERATION_MODELS.find((m) => m.value === genModel)?.info)
                : genProvider === 'openrouter'
                  ? formatOpenRouterPricing(genModel, OPENROUTER_MODELS.find((m) => m.value === genModel)?.info || 'OpenRouter model info')
                  : formatGeminiPricing(genModel, GEMINI_MODELS.find((m) => m.value === genModel)?.info || 'Google Gemini model info')
              }
            </span>
          </div>

          {genProvider === 'gemini' && (
            <div className="field">
              <label>API Key</label>
              <input type="password" value={genApiKeys.gemini || ''} onChange={(e) => setGenApiKeys((prev) => ({ ...prev, gemini: e.target.value }))} placeholder="Enter Gemini API key (or leave blank to use server env)" />
              <span className="field-hint">Your Google AI Studio API key — get one at <strong>ai.google.dev</strong>. Optional if <code>GEMINI_API_KEY</code> is set as a server secret (via Wrangler or environment variable).</span>
            </div>
          )}

          {genProvider === 'openrouter' && (
            <div className="field">
              <label>API Key</label>
              <input type="password" value={genApiKeys.openrouter || ''} onChange={(e) => setGenApiKeys((prev) => ({ ...prev, openrouter: e.target.value }))} placeholder="Enter OpenRouter API key (or leave blank to use server env)" />
              <span className="field-hint">Your OpenRouter API key — get one at <strong>openrouter.ai/keys</strong>. Optional if <code>OPENROUTER_API_KEY</code> is set as a server secret (via Wrangler or environment variable).</span>
            </div>
          )}

          <div className="field">
            <label>Max Tokens: {genMaxTokens}</label>
            <input type="range" min={64} max={4096} step={64} value={genMaxTokens} onChange={(e) => setGenMaxTokens(Number(e.target.value))} />
            <span className="field-hint">Base output token allowance for default-sized generation. Larger sentence counts or sentence lengths auto-scale upward; higher values increase cost and latency.</span>
          </div>

          <div className="field">
            <label>Temperature: {genTemperature.toFixed(2)}</label>
            <input type="range" min={0} max={2} step={0.05} value={genTemperature} onChange={(e) => setGenTemperature(Number(e.target.value))} />
            <span className="field-hint">Controls randomness: 0 = fully deterministic, 1+ = more creative and varied. 0.6–0.8 is a good balance for language practice sentences.</span>
          </div>

          {(genProvider === 'gemini' || genProvider === 'openrouter') && (
            <>
              <div className="field">
                <label>Top P: {genTopP.toFixed(2)}</label>
                <input type="range" min={0} max={1} step={0.05} value={genTopP} onChange={(e) => setGenTopP(Number(e.target.value))} />
                <span className="field-hint">Nucleus sampling: only the smallest set of tokens whose cumulative probability exceeds this threshold are considered. Lower = more focused, higher = more diverse.</span>
              </div>
              <div className="field">
                <label>Top K: {genTopK}</label>
                <input type="range" min={1} max={100} step={1} value={genTopK} onChange={(e) => setGenTopK(Number(e.target.value))} />
                <span className="field-hint">Limits sampling to the K most likely next tokens. Lower = more focused and predictable, higher = broader vocabulary choices.</span>
              </div>
              {genProvider === 'gemini' && isGeminiThinkingBudgetModel(genModel) && (
                <>
                  <div className="field field-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={genThinkingEnabled}
                        onChange={(e) => setGenThinkingEnabled(e.target.checked)}
                      />
                      Enable thinking (reasoning)
                    </label>
                    <span className="field-hint">When enabled, the model uses internal reasoning before responding. This consumes additional output tokens but may improve quality for complex tasks. Disabled by default to maximise usable output.</span>
                  </div>
                  {genThinkingEnabled && (
                    <div className="field">
                      <label>Thinking Budget: {genThinkingBudget} tokens</label>
                      <input type="range" min={128} max={8192} step={128} value={genThinkingBudget} onChange={(e) => setGenThinkingBudget(Number(e.target.value))} />
                      <span className="field-hint">Total output + thinking budget: {genTotalTokensWithThinking} tokens.</span>
                      <span className="field-hint">Maximum tokens the model can use for internal reasoning. Higher values allow deeper thinking but increase latency and cost.</span>
                    </div>
                  )}
                </>
              )}
              {genProvider === 'gemini' && isGeminiThinkingLevelModel(genModel) && (
                <div className="field">
                  <label>Thinking Level</label>
                  <select
                    value={normalizeGeminiThinkingLevel(genModel, genThinkingLevel)}
                    onChange={(e) => setGenThinkingLevel(e.target.value as GeminiThinkingLevel)}
                  >
                    {genThinkingLevelOptions.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">Gemini 3 uses relative thinking levels instead of token budgets. Gemini 3.7 Flash starts at low; Flash-Lite also supports minimal.</span>
                </div>
              )}
            </>
          )}

          <div className="field">
            <span className="field-hint">
              Dynamic prompt fragments are editable here instead of hidden in code. Tense, topic, and case controls render through their instruction templates before being inserted into the word or verb prompt templates.
            </span>
          </div>

          <div className="field">
            <label>Word Sentence Prompt Template</label>
            <textarea
              rows={10}
              value={generatePromptTemplate || promptDefaults?.generatePromptTemplate || ''}
              onChange={(e) => setGeneratePromptTemplate(e.target.value)}
              placeholder="Default prompt template"
            />
            <span className="field-hint">
              Template for generating word-based sentences. Placeholders: {'{sourceLanguage}'}, {'{wordList}'}, {'{sentenceCount}'}, {'{wordsPerSentence}'}, {'{tenseInstruction}'}, {'{topicInstruction}'}, {'{caseInstruction}'}. The instruction placeholders are rendered from the editable templates below. Leave blank to use the server default.
            </span>
          </div>

          <div className="field">
            <label>Verb Practice Prompt Template</label>
            <textarea
              rows={10}
              value={verbPromptTemplate || promptDefaults?.verbPromptTemplate || ''}
              onChange={(e) => setVerbPromptTemplate(e.target.value)}
              placeholder="Default verb prompt template"
            />
            <span className="field-hint">
              Template for verb conjugation practice sentences. Placeholders: {'{sourceLanguage}'}, {'{verbList}'}, {'{sentenceCount}'}, {'{wordsPerSentence}'}, {'{tenseInstruction}'}, {'{topicInstruction}'}, {'{caseInstruction}'}. The instruction placeholders are rendered from the editable templates below. Leave blank to use the server default.
            </span>
          </div>

          <div className="field">
            <label>Tense Instruction Template</label>
            <textarea
              rows={4}
              value={tenseInstructionTemplate || promptDefaults?.tenseInstructionTemplate || ''}
              onChange={(e) => setTenseInstructionTemplate(e.target.value)}
              placeholder="Default tense instruction template"
            />
            <span className="field-hint">
              Shared template for the {'{tenseInstruction}'} placeholder. Placeholders: {'{tenseSummary}'}, {'{tenseInstruction}'} (legacy full sentence). Leave blank to use the server default.
            </span>
          </div>

          <div className="field">
            <label>Topic Instruction Template</label>
            <textarea
              rows={4}
              value={topicInstructionTemplate || promptDefaults?.topicInstructionTemplate || ''}
              onChange={(e) => setTopicInstructionTemplate(e.target.value)}
              placeholder="Default topic instruction template"
            />
            <span className="field-hint">
              Shared template for the {'{topicInstruction}'} placeholder. Placeholders: {'{topic}'}, {'{topicQuoted}'}, {'{topicInstruction}'} (legacy full sentence). It is omitted entirely when no topic is set. Leave blank to use the server default.
            </span>
          </div>

          <div className="field">
            <label>Case Instruction Template</label>
            <textarea
              rows={4}
              value={caseInstructionTemplate}
              onChange={(e) => setCaseInstructionTemplate(e.target.value)}
            />
            <span className="field-hint">
              Editable template for the {'{caseInstruction}'} placeholder. Placeholders: {'{case}'}, {'{caseSummary}'}, {'{caseInstruction}'} (legacy full sentence). It is omitted entirely when no case is selected.
            </span>
          </div>
        </section>

        {/* ── Translation Section ── */}
        <section className="settings-section">
          <h2>{t(language, 'settingsPage.translation')}</h2>

          <div className="field">
            <label>Provider</label>
            <select value={transProvider} onChange={(e) => handleTransProviderChange(e.target.value as AIProviderType)}>
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <span className="field-hint">Choose the AI backend for {sourceLanguageName} → Russian translation. M2M100 (Cloudflare) translates directly between language pairs without an instruction interface. Gemini and OpenRouter LLMs use a prompt-based approach which may be more context-aware.</span>
          </div>

          <div className="field">
            <label>Model</label>
            <select
              value={transModel}
              onChange={(e) => {
                const model = e.target.value;
                setTransModel(model);
                setTransThinkingLevel(normalizeGeminiThinkingLevel(model, transThinkingLevel));
              }}
            >
              {transModelOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <span className="field-hint">
              {transProvider === 'cloudflare'
                ? formatCFPricing(transModel, CF_TRANSLATION_MODELS.find((m) => m.value === transModel)?.info)
                : transProvider === 'openrouter'
                  ? formatOpenRouterPricing(transModel, OPENROUTER_MODELS.find((m) => m.value === transModel)?.info || 'OpenRouter model info')
                  : formatGeminiPricing(transModel, GEMINI_MODELS.find((m) => m.value === transModel)?.info || 'Google Gemini model info')
              }
            </span>
          </div>

          {transProvider === 'gemini' && (
            <div className="field">
              <label>API Key</label>
              <input type="password" value={transApiKeys.gemini || ''} onChange={(e) => setTransApiKeys((prev) => ({ ...prev, gemini: e.target.value }))} placeholder="Enter Gemini API key (or leave blank to use server env)" />
              <span className="field-hint">Can be the same key as generation. Optional if <code>GEMINI_API_KEY</code> is configured as a server secret.</span>
            </div>
          )}

          {transProvider === 'openrouter' && (
            <div className="field">
              <label>API Key</label>
              <input type="password" value={transApiKeys.openrouter || ''} onChange={(e) => setTransApiKeys((prev) => ({ ...prev, openrouter: e.target.value }))} placeholder="Enter OpenRouter API key (or leave blank to use server env)" />
              <span className="field-hint">Can be the same key as generation. Optional if <code>OPENROUTER_API_KEY</code> is configured as a server secret.</span>
            </div>
          )}

          {!isM2M && (
            <>
              <div className="field">
                <label>Temperature: {transTemperature.toFixed(2)}</label>
                <input type="range" min={0} max={2} step={0.05} value={transTemperature} onChange={(e) => setTransTemperature(Number(e.target.value))} />
                <span className="field-hint">Keep low (0.1–0.4) for accurate, literal translations. Higher values introduce more paraphrasing and creative phrasing.</span>
              </div>

              <div className="field">
                <label>Max Tokens: {transMaxTokens}</label>
                <input type="range" min={64} max={4096} step={64} value={transMaxTokens} onChange={(e) => setTransMaxTokens(Number(e.target.value))} />
                <span className="field-hint">Maximum output tokens for the translated text. Should be at least as large as the source text token count.</span>
              </div>
            </>
          )}

          {(transProvider === 'gemini' || transProvider === 'openrouter') && (
            <>
              <div className="field">
                <label>Top P: {transTopP.toFixed(2)}</label>
                <input type="range" min={0} max={1} step={0.05} value={transTopP} onChange={(e) => setTransTopP(Number(e.target.value))} />
                <span className="field-hint">Nucleus sampling threshold for translation. Lower values keep output closer to the most probable words — useful for avoiding translation drift.</span>
              </div>
              <div className="field">
                <label>Top K: {transTopK}</label>
                <input type="range" min={1} max={100} step={1} value={transTopK} onChange={(e) => setTransTopK(Number(e.target.value))} />
                <span className="field-hint">Limits sampling to the K most probable next tokens. Lower values reduce variability in translated output.</span>
              </div>
              {transProvider === 'gemini' && isGeminiThinkingBudgetModel(transModel) && (
                <>
                  <div className="field field-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={transThinkingEnabled}
                        onChange={(e) => setTransThinkingEnabled(e.target.checked)}
                      />
                      Enable thinking (reasoning)
                    </label>
                    <span className="field-hint">When enabled, the model uses internal reasoning before translating. Disabled by default.</span>
                  </div>
                  {transThinkingEnabled && (
                    <div className="field">
                      <label>Thinking Budget: {transThinkingBudget} tokens</label>
                      <input type="range" min={128} max={8192} step={128} value={transThinkingBudget} onChange={(e) => setTransThinkingBudget(Number(e.target.value))} />
                      <span className="field-hint">Total output + thinking budget: {transTotalTokensWithThinking} tokens.</span>
                      <span className="field-hint">Maximum tokens the model can use for internal reasoning during translation.</span>
                    </div>
                  )}
                </>
              )}
              {transProvider === 'gemini' && isGeminiThinkingLevelModel(transModel) && (
                <div className="field">
                  <label>Thinking Level</label>
                  <select
                    value={normalizeGeminiThinkingLevel(transModel, transThinkingLevel)}
                    onChange={(e) => setTransThinkingLevel(e.target.value as GeminiThinkingLevel)}
                  >
                    {transThinkingLevelOptions.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">Gemini 3 uses relative thinking levels instead of token budgets. Gemini 3.7 Flash starts at low; Flash-Lite also supports minimal.</span>
                </div>
              )}
            </>
          )}

          {isM2M && (
            <div className="field-info">
              ℹ️ M2M100 is a dedicated seq-to-seq translation model trained on 100+ language pairs. No prompt or temperature controls are needed — it maps directly between languages without an instruction interface.
            </div>
          )}

          {!isM2M && (
            <div className="field">
              <label>Translation Prompt Template</label>
              <textarea
                rows={6}
                value={translatePromptTemplate || promptDefaults?.translatePromptTemplate || ''}
                onChange={(e) => setTranslatePromptTemplate(e.target.value)}
                placeholder="Default translation prompt template"
              />
              <span className="field-hint">
                Prompt template sent to the LLM for translation. Placeholders: {'{sourceLanguage}'}, {'{sentences}'}. The model should return a JSON array of translated strings in the same order.
              </span>
            </div>
          )}
        </section>

        {/* ── Display Preferences Section ── */}
        <section className="settings-section">
          <h2>{t(language, 'settingsPage.display')}</h2>
          <div className="field field-checkbox">
            <label>
              <input
                type="checkbox"
                checked={topicEnabled}
                onChange={(e) => onTopicEnabledChange?.(e.target.checked)}
              />
              Show topic controls on the main page
            </label>
            <span className="field-hint">Hides the topic selector and remember checkbox from the main page until you want to use topic-guided generation.</span>
          </div>
          <div className="field field-checkbox">
            <label>
              <input
                type="checkbox"
                checked={showModelLabel}
                onChange={(e) => setShowModelLabel(e.target.checked)}
              />
              Show model label above generated sentences
            </label>
            <span className="field-hint">When enabled, a small label with the model name and provider will be displayed above each batch of generated sentences.</span>
          </div>
          <div className="field field-checkbox">
            <label>
              <input
                type="checkbox"
                checked={showDebugInfo}
                onChange={(e) => setShowDebugInfo(e.target.checked)}
              />
              Show debug info
            </label>
            <span className="field-hint">When enabled, an expandable "Debug Info" section showing the prompt, model parameters, and token usage will appear after each LLM response.</span>
          </div>
        </section>
      </div>

      <div className="settings-actions">
        <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
          {saving ? t(language, 'settingsPage.saving') : saved ? t(language, 'settingsPage.saved') : t(language, 'settingsPage.save')}
        </button>
        <button className="btn btn-outline btn-lg btn-reset" onClick={handleReset}>
          {t(language, 'settingsPage.reset')}
        </button>
      </div>
    </div>
  );
}
